import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Field } from "@/pages/Companies";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Zap, Pencil, Trash2, ArrowRight, Mail, Cog, Sparkles, Loader2 } from "lucide-react";
import { AutomationAiChat } from "@/components/AutomationAiChat";
import { STAGE_LABELS, STAGE_ORDER } from "@/lib/constants";

const EMPTY = {
  name: "", enabled: true, workflow: "deals", trigger_event: "deal_stage_changed", trigger_stage: "won",
  action_type: "create_task", action_title: "Follow-up", action_description: "",
  action_offset_days: 1, action_priority: "medium", assign_to: null, target_stage: null, ai_generated: false,
  email_to: "", email_subject: "", email_body: "",
};

const SYSTEM_DESC = {
  welcome_client: { icon: Mail, when: "a new client is added to the Client Database", then: "email them a welcome message with their personal client-portal link" },
  welcome_booking: { icon: Mail, when: "a booking with a client email is saved in the Scheduler", then: "email a welcome + portal link (the client record is auto-created if they're new)" },
  measure_followup: { icon: Mail, when: "a measure-up / site quote booking is marked completed", then: "email the client \u201cthanks for the opportunity \u2014 your quote is on its way\u201d" },
  enquiry_callback: { icon: Cog, when: "a new enquiry arrives (web form, email capture or new client)", then: "auto-book a call-back slot on the Scheduler" },
  deal_won_job: { icon: Cog, when: "a deal is moved to Won", then: "create the install job (with deposit tracking, progress stages and BOM) linked to the deal" },
  deal_won_email: { icon: Mail, when: "a deal is moved to Won", then: "email the admin a deal-won notification" },
  quote_accept_pipeline: { icon: Cog, when: "a quote is accepted (portal or in-app)", then: "generate the material take-off for To Order, auto blueprints and workshop fabrication items" },
  deposit_task: { icon: Cog, when: "the install job is created", then: "create a 'collect 50% deposit' team task" },
  stage_tasks: { icon: Cog, when: "the deposit is paid or a progress stage completes", then: "create the next progress-stage task for the team" },
};

const WF_LABELS = { deals: "Deals", clients: "Clients", enquiries: "Enquiries", quotes: "Quotes", jobs: "Install Jobs", scheduler: "Scheduler", custom: "Custom events" };

export default function Automation() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiResult, setAiResult] = useState(null); // { rules, notes, unsupported }
  const [chatOpen, setChatOpen] = useState(false);
  const [sysEdit, setSysEdit] = useState(null);

  const { data: rules = [] } = useQuery({ queryKey: ["automations"], queryFn: async () => (await api.get("/automations")).data });
  const { data: catalog } = useQuery({ queryKey: ["automation-catalog"], queryFn: async () => (await api.get("/automations/catalog")).data });
  const systemRules = rules.filter((r) => r.system_key);
  const customRules = rules.filter((r) => !r.system_key);
  const eventGroups = catalog?.events || [];
  const staff = catalog?.staff || [];
  const eventLabel = (v) => {
    for (const g of eventGroups) for (const e of g.events) if (e.value === v) return e.label;
    return v;
  };
  const eventWorkflow = (v) => {
    for (const g of eventGroups) if (g.events.some((e) => e.value === v)) return g.workflow;
    return "deals";
  };

  const save = useMutation({
    mutationFn: async (body) => {
      const payload = { ...body, action_offset_days: parseFloat(body.action_offset_days) || 0, workflow: eventWorkflow(body.trigger_event) };
      if (payload.trigger_event !== "deal_stage_changed") payload.trigger_stage = null;
      if (payload.action_type === "email_staff" && typeof payload.email_to === "string")
        payload.email_to = payload.email_to.split(",").map((s) => s.trim()).filter(Boolean);
      if (body.id) return (await api.patch(`/automations/${body.id}`, payload)).data;
      return (await api.post("/automations", payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["automations"] }); setOpen(false); toast.success("Saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const toggle = useMutation({
    mutationFn: async ({ id, enabled }) => (await api.patch(`/automations/${id}`, { enabled })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["automations"] }),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/automations/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["automations"] }),
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not delete"),
  });

  const draftAi = async () => {
    if (!aiPrompt.trim()) return;
    setAiDrafting(true);
    setAiResult(null);
    try {
      const { data } = await api.post("/automations/ai-draft", { prompt: aiPrompt.trim() });
      setAiResult(data);
      if (data.rules?.length) {
        setEditing((prev) => ({ ...EMPTY, ...prev, ...data.rules[0] }));
        toast.success(data.rules.length === 1 ? "Rule drafted — review it below, then save" : `${data.rules.length} rules drafted — rule 1 loaded below for review`);
      } else {
        toast.warning("The AI couldn't build that with the available blocks — see what it said below");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI drafting failed — try again");
    } finally {
      setAiDrafting(false);
    }
  };

  const [savingAll, setSavingAll] = useState(false);
  const saveAllDrafts = async () => {
    setSavingAll(true);
    try {
      for (const r of aiResult.rules) {
        const { assign_to_name, ...payload } = r;
        await api.post("/automations", { ...payload, workflow: eventWorkflow(r.trigger_event) });
      }
      qc.invalidateQueries({ queryKey: ["automations"] });
      toast.success(`${aiResult.rules.length} rules saved`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save all rules");
    } finally {
      setSavingAll(false);
    }
  };

  const customByWf = {};
  for (const r of customRules) {
    const wf = r.workflow || eventWorkflow(r.trigger_event);
    (customByWf[wf] = customByWf[wf] || []).push(r);
  }

  const whenText = (r) => r.trigger_event === "deal_stage_changed"
    ? <>deal moves to <b className="text-cyan-600">{STAGE_LABELS[r.trigger_stage] || r.trigger_stage || "any stage"}</b></>
    : <b className="text-cyan-600">{eventLabel(r.trigger_event)}</b>;
  const thenText = (r) => {
    if (r.action_type === "email_staff") return <>email <b>{(r.email_to || []).join(", ")}</b> &ldquo;{r.email_subject || r.action_title}&rdquo;</>;
    if (r.action_type === "send_webex") return <>send Webex message &ldquo;{r.action_title}&rdquo;</>;
    if (r.action_type === "notify_user") return <>push notification &ldquo;{r.action_title}&rdquo; → <b>{staff.find((s) => s.id === r.assign_to)?.name || "acting user"}</b></>;
    if (r.action_type === "set_deal_stage") return <>move the deal to <b className="text-cyan-600">{STAGE_LABELS[r.target_stage] || r.target_stage}</b></>;
    return <>create <b>{r.action_type.replace("create_", "")}</b> &ldquo;{r.action_title}&rdquo; in {r.action_offset_days}d ({r.action_priority}{r.assign_to ? ` → ${staff.find((s) => s.id === r.assign_to)?.name || "staff"}` : ""})</>;
  };

  return (
    <div>
      <PageHeader
        title="Automation Rules"
        subtitle="Every built-in automation is editable here, plus your own custom trigger → action rules per workflow."
        breadcrumb={["System", "Automation"]}
        actions={
          <div className="flex items-center gap-2">
            <Button data-testid="rule-ai-chat-btn" variant="outline" onClick={() => setChatOpen(true)}
              className="rounded-sm h-10 border-violet-300 text-violet-700 hover:bg-violet-50">
              <Sparkles size={15} className="mr-2" /> AI assistant
            </Button>
            <Button data-testid="new-automation-btn" onClick={() => { setEditing({ ...EMPTY }); setAiPrompt(""); setAiResult(null); setOpen(true); }} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
              <Plus size={16} className="mr-2" /> New rule
            </Button>
          </div>
        }
      />
      <div className="px-6 lg:px-10 py-6 space-y-6">
        {systemRules.length > 0 && (
          <div className="space-y-3">
            <div className="stamp-label text-cyan-700">Built-in automations (toggle on / off)</div>
            {systemRules.map((r) => {
              const d = SYSTEM_DESC[r.system_key] || {};
              const Icon = d.icon || Mail;
              return (
                <Card key={r.id} className="rounded-sm border-slate-200 industrial-shadow p-5" data-testid={`automation-${r.system_key}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${r.enabled ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"}`}>
                        <Icon size={17} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                          {r.name}
                          <Badge variant="outline" className="rounded-sm border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px] uppercase tracking-wider">System</Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                          <Badge variant="outline" className="rounded-sm border-slate-300 bg-white">WHEN</Badge>
                          <span className="text-slate-700">{d.when}</span>
                          <ArrowRight size={12} className="text-slate-400" />
                          <Badge variant="outline" className="rounded-sm border-slate-300 bg-white">THEN</Badge>
                          <span className="text-slate-700">{d.then}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {["welcome_client", "measure_followup"].includes(r.system_key) && (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`edit-system-${r.system_key}`}
                          onClick={() => setSysEdit({ ...r })}><Pencil size={13} /></Button>
                      )}
                      <Switch data-testid={`toggle-automation-${r.system_key}`} checked={r.enabled} onCheckedChange={(v) => toggle.mutate({ id: r.id, enabled: v })} />
                    </div>
                  </div>
                </Card>
              );
            })}
            <p className="text-[11px] text-slate-400">Emails send via Resend when the API key is set; until then they're logged in Email → Logs (mock mode).</p>
          </div>
        )}

        <div className="space-y-3">
          <div className="stamp-label text-slate-500">Custom rules by workflow</div>
          {Object.entries(customByWf).map(([wf, list]) => (
            <div key={wf} className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mt-2">{WF_LABELS[wf] || wf}</div>
              {list.map((r) => (
                <Card key={r.id} className="rounded-sm border-slate-200 industrial-shadow p-5" data-testid={`automation-${r.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${r.enabled ? "bg-cyan-600 text-white" : "bg-slate-200 text-slate-500"}`}>
                        <Zap size={17} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                          {r.name}
                          {r.ai_generated && <Badge data-testid={`ai-badge-${r.id}`} variant="outline" className="rounded-sm border-violet-300 bg-violet-50 text-violet-700 text-[10px] uppercase tracking-wider"><Sparkles size={9} className="mr-1" /> AI</Badge>}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                          <Badge variant="outline" className="rounded-sm border-slate-300 bg-white">WHEN</Badge>
                          <span className="text-slate-700">{whenText(r)}</span>
                          <ArrowRight size={12} className="text-slate-400" />
                          <Badge variant="outline" className="rounded-sm border-slate-300 bg-white">THEN</Badge>
                          <span className="text-slate-700">{thenText(r)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch data-testid={`toggle-automation-${r.id}`} checked={r.enabled} onCheckedChange={(v) => toggle.mutate({ id: r.id, enabled: v })} />
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setEditing({ ...EMPTY, ...r }); setOpen(true); }}><Pencil size={13} /></Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600" onClick={() => del.mutate(r.id)}><Trash2 size={13} /></Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ))}
          {customRules.length === 0 && (
            <Card className="rounded-sm border-2 border-dashed border-slate-200 p-12 text-center">
              <Zap size={28} className="text-slate-300 mx-auto mb-3" />
              <div className="font-display text-lg font-semibold text-slate-900">No custom rules yet</div>
              <div className="text-sm text-slate-500 mt-1">Pick any event across Deals, Clients, Enquiries, Quotes, Install Jobs or the Scheduler and choose what happens.</div>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-xl">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing?.id ? "Edit rule" : "New rule"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              {!editing.id && (
                <div className="border border-violet-200 bg-violet-50/40 p-4 space-y-2 rounded-sm">
                  <div className="stamp-label text-violet-700 flex items-center gap-1.5"><Sparkles size={12} /> Describe it — AI drafts the rule</div>
                  <Textarea
                    rows={3}
                    data-testid="ai-rule-prompt"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder='e.g. "When a deal is won, remind Allan 2 days later to order materials, and book a client call after 5 days"'
                    className="rounded-sm text-sm bg-white"
                  />
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-[10px] text-slate-500 flex-1 min-w-[160px]">The AI only uses the safe building blocks below — you review before anything is saved.</p>
                    <Button size="sm" onClick={draftAi} disabled={aiDrafting || !aiPrompt.trim()} data-testid="ai-draft-btn"
                      className="rounded-sm bg-violet-600 hover:bg-violet-700 text-white">
                      {aiDrafting ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Sparkles size={13} className="mr-1.5" />}
                      Draft with AI
                    </Button>
                  </div>
                  {aiResult?.notes && <div className="text-xs text-violet-900 border border-violet-200 bg-white rounded-sm p-2" data-testid="ai-draft-notes">{aiResult.notes}</div>}
                  {aiResult?.unsupported?.length > 0 && (
                    <div className="text-xs text-amber-900 border border-amber-300 bg-amber-50 rounded-sm p-2 space-y-1" data-testid="ai-draft-unsupported">
                      <div className="font-semibold">Not possible with the current building blocks:</div>
                      {aiResult.unsupported.map((u, i) => <div key={i}>• {u}</div>)}
                      <div className="text-[10px] text-amber-700">Want one of these added? Ask the developer to whitelist a new trigger/action.</div>
                    </div>
                  )}
                  {aiResult?.rules?.length > 1 && (
                    <div className="text-xs border border-violet-200 bg-white rounded-sm p-2 space-y-1.5" data-testid="ai-draft-multi">
                      <div className="font-semibold text-violet-900">{aiResult.rules.length} rules drafted:</div>
                      {aiResult.rules.map((r, i) => (
                        <div key={i} className="flex items-center justify-between gap-2">
                          <span className="truncate text-slate-700">{i + 1}. {r.name}</span>
                          <button type="button" className="text-violet-700 underline shrink-0" data-testid={`ai-load-rule-${i}`}
                            onClick={() => setEditing({ ...EMPTY, ...r })}>review</button>
                        </div>
                      ))}
                      <Button size="sm" onClick={saveAllDrafts} disabled={savingAll} data-testid="ai-save-all-btn"
                        className="rounded-sm bg-violet-600 hover:bg-violet-700 text-white w-full mt-1">
                        {savingAll ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Plus size={13} className="mr-1.5" />}
                        Save all {aiResult.rules.length} rules
                      </Button>
                    </div>
                  )}
                </div>
              )}
              <Field label="Rule name" required><Input data-testid="automation-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
              <div className="border border-slate-200 p-4 bg-slate-50/40 space-y-3">
                <div className="stamp-label text-slate-500">WHEN</div>
                <Field label="Trigger event">
                  <Select value={editing.trigger_event} onValueChange={(v) => setEditing({ ...editing, trigger_event: v })}>
                    <SelectTrigger data-testid="automation-event-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {eventGroups.map((g) => (
                        g.events.map((e) => (
                          <SelectItem key={e.value} value={e.value}>{g.label} — {e.label}</SelectItem>
                        ))
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                {editing.trigger_event === "deal_stage_changed" && (
                  <Field label="Trigger stage">
                    <Select value={editing.trigger_stage || "won"} onValueChange={(v) => setEditing({ ...editing, trigger_stage: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGE_ORDER.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                )}
              </div>
              <div className="border border-slate-200 p-4 bg-cyan-50/40 space-y-3">
                <div className="stamp-label text-cyan-700">THEN</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Action">
                    <Select value={editing.action_type} onValueChange={(v) => setEditing({ ...editing, action_type: v, target_stage: v === "set_deal_stage" ? (editing.target_stage || "won") : editing.target_stage })}>
                      <SelectTrigger data-testid="automation-action-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="create_task">Create Task</SelectItem>
                        <SelectItem value="create_call">Create Call</SelectItem>
                        <SelectItem value="create_meeting">Create Meeting</SelectItem>
                        <SelectItem value="send_webex">Send Webex message</SelectItem>
                        <SelectItem value="notify_user">Send push notification</SelectItem>
                        <SelectItem value="set_deal_stage">Move deal to stage</SelectItem>
                        <SelectItem value="email_staff">Send email to staff</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  {["create_task", "create_call", "create_meeting"].includes(editing.action_type) && (
                    <Field label="Priority">
                      <Select value={editing.action_priority} onValueChange={(v) => setEditing({ ...editing, action_priority: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                  {editing.action_type === "set_deal_stage" && (
                    <Field label="Move deal to">
                      <Select value={editing.target_stage || "won"} onValueChange={(v) => setEditing({ ...editing, target_stage: v })}>
                        <SelectTrigger data-testid="automation-target-stage-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{STAGE_ORDER.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                  )}
                </div>
                {editing.action_type !== "set_deal_stage" && (
                  <>
                    <Field label={editing.action_type === "send_webex" ? "Message" : editing.action_type === "notify_user" ? "Notification title" : "Title"}>
                      <Input value={editing.action_title} onChange={(e) => setEditing({ ...editing, action_title: e.target.value })} />
                    </Field>
                    <p className="text-[10px] text-slate-400 -mt-2">Placeholders: {"{{client_name}} {{job_number}} {{title}} {{stage}} {{quote_number}} {{value}}"}</p>
                  </>
                )}
                {editing.action_type === "set_deal_stage" && (
                  <p className="text-[10px] text-slate-500">Guardrail: this moves the deal silently — it can never trigger other rules, so no chain reactions.</p>
                )}
                {editing.action_type === "notify_user" && (
                  <>
                    <Field label="Send notification to">
                      <Select value={editing.assign_to || "actor"} onValueChange={(v) => setEditing({ ...editing, assign_to: v === "actor" ? null : v })}>
                        <SelectTrigger data-testid="automation-notify-target-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="actor">Person logged in at the time</SelectItem>
                          {staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Notification message (optional)"><Input value={editing.action_description || ""} onChange={(e) => setEditing({ ...editing, action_description: e.target.value })} /></Field>
                  </>
                )}
                {editing.action_type === "email_staff" && (
                  <>
                    <Field label="Send to (emails, comma-separated)">
                      <Input data-testid="automation-email-to"
                        value={Array.isArray(editing.email_to) ? editing.email_to.join(", ") : (editing.email_to || "")}
                        onChange={(e) => setEditing({ ...editing, email_to: e.target.value })}
                        placeholder="allan@…, accounts@…" />
                    </Field>
                    <Field label="Subject"><Input data-testid="automation-email-subject" value={editing.email_subject || ""} onChange={(e) => setEditing({ ...editing, email_subject: e.target.value })} /></Field>
                    <Field label="Message"><Textarea rows={3} data-testid="automation-email-body" value={editing.email_body || ""} onChange={(e) => setEditing({ ...editing, email_body: e.target.value })} className="rounded-sm text-sm" placeholder="Each line becomes a paragraph. Placeholders work here too." /></Field>
                  </>
                )}
                {["create_task", "create_call", "create_meeting"].includes(editing.action_type) && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Due in (days)"><Input type="number" step="0.5" value={editing.action_offset_days} onChange={(e) => setEditing({ ...editing, action_offset_days: e.target.value })} /></Field>
                      <Field label="Assign to">
                        <Select value={editing.assign_to || "actor"} onValueChange={(v) => setEditing({ ...editing, assign_to: v === "actor" ? null : v })}>
                          <SelectTrigger data-testid="automation-assignee-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="actor">Person logged in at the time</SelectItem>
                            {staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                    <Field label="Description (optional)"><Input value={editing.action_description || ""} onChange={(e) => setEditing({ ...editing, action_description: e.target.value })} /></Field>
                  </>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-automation-btn" onClick={() => {
              if (!editing.name?.trim()) return toast.error("Rule name required");
              save.mutate(editing);
            }} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">Save rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AutomationAiChat open={chatOpen} onOpenChange={setChatOpen} eventLabel={eventLabel}
        eventWorkflow={eventWorkflow} onSaved={() => qc.invalidateQueries({ queryKey: ["automations"] })} />

      <Dialog open={!!sysEdit} onOpenChange={(v) => !v && setSysEdit(null)}>
        <DialogContent className="rounded-sm sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">Edit email — {sysEdit?.name}</DialogTitle></DialogHeader>
          {sysEdit && (
            <div className="space-y-3">
              <Field label="Subject (leave blank for the default)">
                <Input data-testid="system-subject-input" value={sysEdit.subject_override || ""}
                  onChange={(e) => setSysEdit({ ...sysEdit, subject_override: e.target.value })}
                  placeholder="Default CCFI subject is used when blank" />
              </Field>
              <Field label="Message body (leave blank for the default)">
                <Textarea rows={7} data-testid="system-body-input" value={sysEdit.body_override || ""}
                  onChange={(e) => setSysEdit({ ...sysEdit, body_override: e.target.value })}
                  placeholder="Write plain text — each line becomes a paragraph." className="rounded-sm text-sm" />
              </Field>
              <p className="text-[10px] text-slate-400">
                Placeholders: {"{{name}} {{company}}"}.{" "}
                {sysEdit.system_key === "welcome_client" && "The client-portal button is always added below your text. This wording is also used by the Scheduler welcome email."}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setSysEdit(null)}>Cancel</Button>
            <Button data-testid="save-system-edit-btn" className="rounded-sm bg-cyan-600 hover:bg-cyan-700" onClick={async () => {
              try {
                await api.patch(`/automations/${sysEdit.id}`, {
                  subject_override: sysEdit.subject_override || "", body_override: sysEdit.body_override || "" });
                qc.invalidateQueries({ queryKey: ["automations"] });
                toast.success("Email wording updated");
                setSysEdit(null);
              } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
            }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
